import React, { useEffect, useState } from "react";
import { Select, SelectOption, Button, Separator } from "@showitinc/showit-wc";
import DomainActions from "../actions/DomainActions";
import UIActions from "../actions/UIActions";
import UserActions from "../actions/UserActions";
import AppConstants from "../constants/AppConstants";
import { isContributing as getIsContributing, isServiceEnabled } from "../hooks/query/useAccount";
import DesignViewStore from "../stores/DesignViewStore";
import HelpTooltip from "./HelpTooltip";
import useOpenAccount, {
  PATH_SUBSCRIPTION,
  PATH_SUBSCRIBE,
  PATH_HOME,
} from "../hooks/useOpenAccount";
import When from "./utils/When";
import Glyphicon from "./base/Glyphicon";
import useAccessibleAccounts from "../hooks/query/useAccessibleAccounts";
import uiPrefix from "../utils/UiPrefixUtils";
import {
  COLOR_MODE_AUTO,
  COLOR_MODE_LIGHT,
  COLOR_MODE_DARK,
  UI_SCALE_LARGE,
  UI_SCALE_SMALL,
  UI_SCALE_REGULAR,
} from "../constants/UIConstants";
import useStoreState from "./providers/useStoreState";
import styles from "./UserPanel.module.css";
import { goToUserMcShowit } from "../adaptors/NavigationAdaptors";
import useSite from "../hooks/query/useSite";
import { copyToClipboard } from "../utils/AppUtils";
import { showAdminRestoreModal } from "../adaptors/ModalAdaptors";

const columnStyle = { flex: 1, padding: ".5rem" };

const labelStyle = {
  pointerEvents: "none",
  letterSpacing: 0,
};

const WithHelp = ({ tooltip, children }) => (
  <div className="button-with-help">
    {children}
    <HelpTooltip tooltipKey={tooltip} />
  </div>
);

const ActionItem = ({ onClick, glyph = false, icon = false, label, className = "" }) => (
  <div
    className={`${className} listed-page add-page-button`}
    style={{ margin: 0, paddingTop: 5, paddingBottom: 5, textTransform: "none" }}
    onClick={onClick}
  >
    <Glyphicon
      glyph={glyph}
      icon={icon}
      className="listed-page-icon"
      style={icon ? { padding: 8 } : {}}
    />
    <label style={labelStyle}>{label}</label>
  </div>
);

const PanelButton = ({
  onClick,
  glyph = false,
  icon = false,
  label,
  tooltip = false,
  className = "",
}) => {
  const item = (
    <ActionItem icon={icon} glyph={glyph} label={label} onClick={onClick} className={className} />
  );
  return tooltip ? <WithHelp tooltip={tooltip}>{item}</WithHelp> : item;
};

const showSiteChooser = () => {
  UIActions.showAppView({ view: AppConstants.MAIN_VIEW_LIBRARY_SITE_CHOOSER, allowBack: true });
};

const showUserSiteChooser = () => {
  UIActions.showAppView({
    view: AppConstants.MAIN_VIEW_USER_SITE_CHOOSER,
    allowBack: true,
    props: { mode: AppConstants.USER_SITES_VIEW_MODE_MANAGE },
  });
};

const showExportedDesigns = () => {
  UIActions.modalOpen(AppConstants.MODAL_USER_EXPORTS);
};

const showImportDesign = () => {
  UIActions.modalOpen(AppConstants.MODAL_DESIGN_IMPORT);
};

const showFeedback = () => {
  UserActions.triggerSatismeterEvent({ event: "Feedback Click", clickLocation: "User Panel" });
  // UserStore.initSatisMeter("satismeter-form");
};

const UserPanel = ({ user, close }) => {
  const colorMode = useStoreState(DesignViewStore, DesignViewStore.getColorMode);
  const uiScale = useStoreState(DesignViewStore, DesignViewStore.getUIScale);
  const [viewScale, setViewScale] = useState(uiScale);
  const [openSubscribe] = useOpenAccount({ path: PATH_SUBSCRIBE });
  const [openAccountSubscription] = useOpenAccount({ path: PATH_SUBSCRIPTION });
  const [openAccountHome] = useOpenAccount({ path: PATH_HOME });
  const isActive = user.get("isActive");
  const actingContributor = user.get("actingContributor")?.toJS();
  const { accessibleAccounts } = useAccessibleAccounts();
  const hasContributorAccess = accessibleAccounts?.length > 0;
  const isContributing = getIsContributing();
  const [version, setVersion] = useState();
  const userId = user.get("user_id");
  const isAdmin = user.get("isAdmin", false);
  const { site } = useSite();
  const designKey = site?.key;

  useEffect(() => {
    const appInfo = document.getElementById("app-info");
    const { appVersion } = appInfo?.dataset;
    setVersion(appVersion);
  }, []);

  const onChangeColorMode = (mode) => {
    UIActions.changeColorMode(mode);
  };

  const accountFooterStyle = {
    display: "flex",
    justifyContent: hasContributorAccess ? "space-between" : "flex-end",
    borderTop: "0.0625rem solid var(--border-surface-highlight)",
    alignItems: "baseline",
    paddingTop: ".75rem",
  };

  const onSetUIScale = (scale) => {
    UIActions.setUIScale(scale);
    setViewScale(scale);
  };

  const manageSubscription = () => {
    if (isActive) {
      const service = user.get("subscriptionService");
      // TODO remove when all subscriptions are moved to Chargebee
      if (service === AppConstants.SUBSCRIPTION_SERVICE_EDD) {
        UserActions.openStore({
          location: AppConstants.SHOWIT_STORE_SUBSCRIPTION,
          newWindow: true,
        });
      } else if (service === AppConstants.SUBSCRIPTION_SERVICE_CHARGEBEE) {
        openAccountSubscription();
      } else {
        // comped and lifetime
        openAccountHome();
      }
    } else {
      openSubscribe();
    }
  };

  const manageDomains = () => {
    close();
    DomainActions.domainManageOpen();
  };

  const isSurveyActive = isServiceEnabled("satismeter", "satismeter");

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div
          data-e2e={uiPrefix("site-designs-panel")}
          className={uiPrefix("site-designs-panel")}
          style={{ ...columnStyle, borderRight: ".0625rem solid var(--border-surface-highlight)" }}
        >
          <div className="si-c-dialog__title">Site Designs</div>
          <PanelButton
            data-e2e={uiPrefix("site-designs-panel-btn")}
            className={uiPrefix("site-designs-panel-btn")}
            onClick={showUserSiteChooser}
            icon="block-2"
            label={`View${isContributing ? " Their" : " My"} Site Designs`}
          />
          <PanelButton
            data-e2e={uiPrefix("create-new-design-btn")}
            className={uiPrefix("create-new-design-btn")}
            onClick={showSiteChooser}
            glyph="circle_plus"
            label="Create a New Design"
            tooltip="plus-site"
          />
          <PanelButton
            data-e2e={uiPrefix("view-shared-designs-btn")}
            className={uiPrefix("view-shared-designs-btn")}
            onClick={showExportedDesigns}
            glyph="share"
            label="View Shared Designs"
          />
          <PanelButton
            data-e2e={uiPrefix("add-to-library-btn")}
            className={uiPrefix("add-to-library-btn")}
            onClick={showImportDesign}
            glyph="cloud_plus"
            label="Add To Library"
            tooltip="shared-design-add"
          />

          <When it={isAdmin}>
            <Separator />
            <PanelButton
              data-e2e={uiPrefix("mcshowit-btn")}
              className={uiPrefix("mcshowit-btn")}
              label="View User in McShowit"
              glyph="user"
              title={userId}
              onClick={() => goToUserMcShowit(userId)}
            />

            <PanelButton
              data-e2e={uiPrefix("mcshowit-btn")}
              className={uiPrefix("mcshowit-btn")}
              label="Copy Design key"
              glyph="link"
              title={designKey}
              onClick={() => copyToClipboard({ val: designKey, showToast: true, dur: 2000 })}
            />

            <PanelButton
              data-e2e={uiPrefix("mcshowit-btn")}
              className={uiPrefix("mcshowit-btn")}
              glyph="clock"
              label="View Restore Points"
              onClick={showAdminRestoreModal}
            />
          </When>
        </div>

        <div
          data-e2e={uiPrefix("account-settings-panel")}
          className={uiPrefix("account-settings-panel")}
          style={{ ...columnStyle, borderRight: ".0625rem solid var(--border-surface-highlight)" }}
        >
          <div className="si-c-dialog__title">Account Settings</div>
          <When it={!isContributing}>
            <PanelButton
              data-e2e={uiPrefix("manage-account-btn")}
              className={uiPrefix("manage-account-btn")}
              onClick={openAccountHome}
              glyph="user"
              label="Manage Account"
            />
            <PanelButton
              data-e2e={uiPrefix("manage-subscription-btn")}
              className={uiPrefix("manage-subscription-btn")}
              onClick={manageSubscription}
              glyph="calendar"
              label={isActive ? "Subscription" : "Subscribe Now"}
            />
          </When>
          <When it={isActive}>
            <PanelButton
              data-e2e={uiPrefix("manage-domains-btn")}
              className={uiPrefix("manage-domains-btn")}
              onClick={manageDomains}
              glyph="global"
              label="Manage Domain Names"
            />
          </When>
          <When it={isSurveyActive}>
            <PanelButton
              data-e2e={uiPrefix("rate-us-btn")}
              className={uiPrefix("rate-us-btn")}
              onClick={showFeedback}
              glyph="star"
              label="Rate Us"
            />
          </When>
          <p className="instrux">{`App version: ${version}`}</p>
        </div>

        <div
          data-e2e={uiPrefix("account-settings-panel")}
          className={uiPrefix("account-settings-panel")}
          style={{ ...columnStyle }}
        >
          <div className="si-c-dialog__title">Preferences</div>
          <Select
            data-testid="color-mode-select"
            value={colorMode || COLOR_MODE_DARK}
            longLabel
            label="Mode"
            data-intercom-target="user preferences colormode"
          >
            <SelectOption
              onSISelect={() => onChangeColorMode(COLOR_MODE_AUTO)}
              value={COLOR_MODE_AUTO}
            >
              System
            </SelectOption>
            <SelectOption
              onSISelect={() => onChangeColorMode(COLOR_MODE_LIGHT)}
              value={COLOR_MODE_LIGHT}
            >
              Light
            </SelectOption>
            <SelectOption
              onSISelect={() => onChangeColorMode(COLOR_MODE_DARK)}
              value={COLOR_MODE_DARK}
            >
              Dark
            </SelectOption>
          </Select>
          <Select
            value={viewScale}
            longLabel
            label="Density"
            data-intercom-target="user preferences density"
          >
            <SelectOption onSISelect={() => onSetUIScale(UI_SCALE_SMALL)} value={UI_SCALE_SMALL}>
              Small
            </SelectOption>
            <SelectOption
              onSISelect={() => onSetUIScale(UI_SCALE_REGULAR)}
              value={UI_SCALE_REGULAR}
            >
              Regular
            </SelectOption>
            <SelectOption onSISelect={() => onSetUIScale(UI_SCALE_LARGE)} value={UI_SCALE_LARGE}>
              Large
            </SelectOption>
          </Select>
        </div>
      </div>
      <div style={accountFooterStyle}>
        {hasContributorAccess && actingContributor && (
          <div style={{ width: "65%" }}>
            <Select
              type="select"
              label="Signed in as"
              columns=".6fr 2fr"
              propName="contributor"
              className={styles["contributor-select"]}
              value={String(userId)}
              hideTooltip
              onSISelect={({ target: { value } }) => {
                UserActions.switchAccount(Number(value));
              }}
            >
              <SelectOption value={String(actingContributor.id)}>
                {actingContributor.first_name} {actingContributor.last_name} -{" "}
                {actingContributor.email}
              </SelectOption>

              <SelectOption disabled>---------------------------</SelectOption>

              {accessibleAccounts.map(({ ownerUserId, ownerEmail, ownerName }) => (
                <SelectOption key={ownerUserId} value={String(ownerUserId)}>
                  {ownerName} - {ownerEmail}
                </SelectOption>
              ))}
            </Select>
          </div>
        )}
        <div>
          <Button variant="outline" onSIClick={() => close()}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UserPanel;
